import ImageUploadPattern from 'assets/images/ImageUploadPattern.png';

import React, { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
// i18next
import { useTranslation } from 'react-i18next';
// Redux
import { useAppDispatch } from 'hooks/useStore';
import { generateImage } from 'store/quizAgent/quizAgentAsync';
// Types
import MediaFormats from 'types/MediaFormats';
// Icons
import { FaArrowLeft, FaTimes, FaMagic, FaUndo, FaCheck, FaPlus } from 'react-icons/fa';
// Components
import { Portal } from 'components/Portal';
import { Button, IconButton } from 'components/Buttons';
import { Select, Textarea } from 'components/Controls';
import { Loader } from 'components/Utilities';
// Utilities
import {
  mediasInfo, buildEnrichedAiImagePrompt, supportsAiImageShapeOption,
  aiImageStyleOptions, aiImageColorOptions, aiImageShapeOptions,
  AiImageStyle, AiImageShape
} from 'utilities/utilities';

type Props = {
  open: boolean;
  onClose: () => void;
  format: MediaFormats;
  // Lets the user pick the target format inside the dialog (e.g. when generating straight into the media gallery)
  allowFormatChange?: boolean;
  onApply: (imageUrl: string, format: MediaFormats) => void;
}

const formatOptions = (Object.keys(MediaFormats) as Array<keyof typeof MediaFormats>)
  .map((key) => ({ value: MediaFormats[key], label: mediasInfo[MediaFormats[key]].label }));

const isValidHexColor = (value?: string | null): value is string => Boolean(value) && /^#([0-9a-f]{3}){1,2}$/i.test(value as string);

const GenerateImageDialog:React.FC<Props> = ({ open, onClose, format: initialFormat, allowFormatChange = false, onApply }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const formContext = useFormContext();

  const [format, setFormat] = useState<MediaFormats>(initialFormat);
  const { width, height } = mediasInfo[format];
  const supportsShape = supportsAiImageShapeOption(format);

  // The quiz form (when present) may already define a brand color — offer it as a quick option instead of forcing a default.
  const quizPrimaryColor = formContext?.watch('theme.primaryColor');
  const hasQuizPrimaryColor = isValidHexColor(quizPrimaryColor);

  const colorOptions = useMemo(() => {
    if (!hasQuizPrimaryColor) return aiImageColorOptions;
    return [{ value: quizPrimaryColor, label: t('dialogs.generateImage.brandColorLabel') }, ...aiImageColorOptions];
  }, [hasQuizPrimaryColor, quizPrimaryColor, t]);

  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (!open) return;
    const id = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(id);
  }, [open]);

  const [prompt, setPrompt] = useState('');
  const [style, setStyle] = useState<AiImageStyle | null>('flat');
  const [color, setColor] = useState<string | null>(hasQuizPrimaryColor ? quizPrimaryColor : '#F97316');
  const [customColor, setCustomColor] = useState(hasQuizPrimaryColor ? quizPrimaryColor : '#F97316');
  const [shape, setShape] = useState<AiImageShape | null>(supportsShape ? 'square' : null);
  const [isLoading, setIsLoading] = useState(false);
  const [generatedUrl, setGeneratedUrl] = useState<string | null>(null);

  const handleRequestClose = () => {
    setVisible(false);
    setTimeout(onClose, 200);
  }

  const handleChangeFormat = (e: ChangeEvent<HTMLSelectElement>) => {
    setFormat(e.target.value as MediaFormats);
    // The generated image was cropped for the previous format's dimensions
    setGeneratedUrl(null);
  }

  const handleChangePrompt = (e: ChangeEvent<HTMLTextAreaElement>) => setPrompt(e.target.value);
  const toggleStyle = (value: AiImageStyle) => setStyle(prev => prev === value ? null : value);
  const toggleColor = (value: string) => setColor(prev => prev === value ? null : value);
  const toggleShape = (value: AiImageShape) => setShape(prev => prev === value ? null : value);

  const handleChangeCustomColor = (e: ChangeEvent<HTMLInputElement>) => {
    setCustomColor(e.target.value);
    setColor(e.target.value);
  }

  const isPromptEmpty = !prompt.trim();
  const enrichedPrompt = buildEnrichedAiImagePrompt(prompt, style, color, supportsShape ? shape : null);

  const handleGenerate = () => {
    if (isPromptEmpty) return;
    setIsLoading(true);
    // Request at 2x the target dimensions so the source image stays sharp after any downstream scaling/cropping.
    dispatch(generateImage({ prompt: enrichedPrompt, size: `${width * 2}x${height * 2}` })).unwrap()
      .then(({ url }) => setGeneratedUrl(url))
      // Errors already surface to the user via the global notification middleware (see rejected actions) —
      // this catch only prevents an unhandled promise rejection.
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }

  const handleReplace = () => {
    if (!generatedUrl) return;
    onApply(generatedUrl, format);
    handleRequestClose();
  }

  const isCustomColorSelected = Boolean(color) && !colorOptions.some(c => c.value === color);
  const showResult = isLoading || Boolean(generatedUrl);

  if (!open) return null;

  return (
    <Portal id="root-dialog">
      <div
        className="fixed top-0 left-0 w-full h-full flex justify-end"
        style={{ backgroundColor: visible ? 'rgba(0,0,0,0.4)' : 'rgba(0,0,0,0)', transition: 'background-color 200ms ease-out', zIndex: 50 }}
        onClick={(e) => { if (e.target === e.currentTarget) handleRequestClose(); }}
      >
        <div
          className="relative h-full w-full flex flex-col shadow-2xl"
          style={{
            maxWidth: 440,
            backgroundColor: '#252424',
            transform: visible ? 'translateX(0)' : 'translateX(100%)',
            transition: 'transform 220ms ease-out'
          }}
        >
          <div className="flex items-center gap-2 p-4 border-b border-divider flex-shrink-0">
            <IconButton onClick={handleRequestClose} aria-label={t('buttons.back')}><FaArrowLeft /></IconButton>
            <h3 className="text-xl text-white font-semibold flex-grow">{t('dialogs.generateImage.title')}</h3>
            <IconButton onClick={handleRequestClose} aria-label={t('buttons.close')}><FaTimes /></IconButton>
          </div>

          <div className="flex-grow overflow-y-auto p-4">
            {allowFormatChange ? (
              <Select
                id="generate-image-format"
                name="generate-image-format"
                options={formatOptions}
                value={format}
                onChange={handleChangeFormat}
                label={t('labels.format')}
                helperText={mediasInfo[format].hint}
                disabled={isLoading}
              />
            ) : null}
            <Textarea
              id="generate-image-prompt"
              name="generate-image-prompt"
              label={t('dialogs.generateImage.promptLabel')}
              onChange={handleChangePrompt}
              value={prompt}
              rows={3}
              disabled={isLoading}
              required
            />

            <div className="mb-4">
              <span className="block text-white text-sm font-semibold mb-2">{t('dialogs.generateImage.quickStylesLabel')}</span>
              <div className="flex flex-wrap gap-2">
                {aiImageStyleOptions.map(option => (
                  <button
                    key={option.value}
                    type="button"
                    disabled={isLoading}
                    onClick={() => toggleStyle(option.value)}
                    className={`
                      px-3 py-1.5 rounded-full text-sm font-medium border transition-colors
                      ${style === option.value ? 'bg-primary border-primary text-white' : 'border-divider text-gray-300 hover:border-gray-400'}
                    `}
                  >{option.label}</button>
                ))}
              </div>
            </div>

            <div className="mb-4">
              <span className="block text-white text-sm font-semibold mb-2">{t('dialogs.generateImage.colorsLabel')}</span>
              <div className="flex items-center gap-2">
                {colorOptions.map(option => (
                  <button
                    key={option.value}
                    type="button"
                    title={option.label}
                    disabled={isLoading}
                    onClick={() => toggleColor(option.value)}
                    className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{
                      backgroundColor: option.value,
                      boxShadow: color === option.value ? '0 0 0 2px #252424, 0 0 0 4px #ff9000' : 'none'
                    }}
                  >{color === option.value ? <FaCheck className="text-white" size={11} /> : null}</button>
                ))}
                <label
                  title={t('dialogs.generateImage.customColorLabel')}
                  className="relative w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 cursor-pointer text-gray-300 hover:text-white"
                  style={{
                    backgroundColor: isCustomColorSelected ? customColor : 'transparent',
                    border: isCustomColorSelected ? 'none' : '2px dashed rgba(255,255,255,0.3)',
                    boxShadow: isCustomColorSelected ? '0 0 0 2px #252424, 0 0 0 4px #ff9000' : 'none'
                  }}
                >
                  {isCustomColorSelected ? <FaCheck className="text-white" size={11} /> : <FaPlus size={11} />}
                  {/* Positioned (not display:none) so the browser anchors the native color picker on the swatch instead of the viewport corner. */}
                  <input
                    type="color"
                    className="absolute top-0 left-0 w-full h-full opacity-0 cursor-pointer"
                    value={customColor}
                    onChange={handleChangeCustomColor}
                    disabled={isLoading}
                  />
                </label>
              </div>
            </div>

            {supportsShape ? (
              <div className="mb-4">
                <span className="block text-white text-sm font-semibold mb-2">{t('dialogs.generateImage.shapeLabel')}</span>
                <div className="flex flex-wrap gap-2">
                  {aiImageShapeOptions.map(option => (
                    <button
                      key={option.value}
                      type="button"
                      disabled={isLoading}
                      onClick={() => toggleShape(option.value)}
                      className={`
                        px-3 py-1.5 rounded-full text-sm font-medium border transition-colors
                        ${shape === option.value ? 'bg-primary border-primary text-white' : 'border-divider text-gray-300 hover:border-gray-400'}
                      `}
                    >{option.label}</button>
                  ))}
                </div>
              </div>
            ) : null}

            {showResult ? (
              <div className="mt-2 pt-4 border-t border-divider">
                <span className="block text-white text-sm font-semibold mb-2">{t('dialogs.generateImage.generatedLabel')}</span>
                <div
                  className="relative rounded overflow-hidden mx-auto"
                  style={{
                    backgroundImage: `url(${ImageUploadPattern})`,
                    boxShadow: 'inset 0 0 16px rgba(0,0,0,0.75)',
                    width: '100%',
                    aspectRatio: `${width} / ${height}`,
                    maxHeight: 320
                  }}
                >
                  {isLoading ? (
                    <div className="absolute top-0 left-0 w-full h-full flex justify-center items-center" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                      <Loader />
                    </div>
                  ) : null}
                  {!isLoading && generatedUrl ? (
                    <img className="absolute top-0 left-0 w-full h-full object-contain" src={generatedUrl} alt="" />
                  ) : null}
                </div>
              </div>
            ) : null}
          </div>

          <div className="p-4 border-t border-divider flex gap-2 flex-shrink-0">
            {generatedUrl && !isLoading ? (
              <React.Fragment>
                <Button variant="outlined" className="flex-grow" onClick={handleGenerate} disabled={isPromptEmpty}>
                  <FaUndo className="inline-block mr-2 -mt-0.5" />{t('dialogs.generateImage.regenerateButton')}
                </Button>
                <Button variant="contained" className="flex-grow" onClick={handleReplace}>
                  <FaCheck className="inline-block mr-2 -mt-0.5" />{t('buttons.apply')}
                </Button>
              </React.Fragment>
            ) : (
              <Button variant="contained" className="w-full" loading={isLoading} onClick={handleGenerate} disabled={isPromptEmpty}>
                <FaMagic className="inline-block mr-2 -mt-0.5" />{t('dialogs.generateImage.generateButton')}
              </Button>
            )}
          </div>
        </div>
      </div>
    </Portal>
  )
}

export default GenerateImageDialog;
